'use client';

import { ActionButtonWithDescription } from '@/shared/components/action-button-with-description';
import { PurchaseOption } from '@/features/games/shared/types/game-data';
import { PurchaseDescription } from '@/features/games/internal/components/game-detail/download-game/purchase-game-button-description';
import Loader from '@/shared/components/loader';
import { PriceIncludingWildMembership, PromotionData } from '@/shared/types/prices';
import { getPriceByType } from '@/features/games/shared/helpers/get-price-by-type';
import { REDIRECT_CHECKOUT_ROUTE } from '@/shared/constants/routes';

type Props = {
  title: string;
  purchaseOption: PurchaseOption;
  prices: PriceIncludingWildMembership[];
  isLoading: boolean;
  inCart: boolean;
  isMember: boolean;
  blocked?: boolean;
  alreadyInCartText: string;
  addToCartAction: (
    purchaseOptionType: PurchaseOption,
    price: PriceIncludingWildMembership,
    promotion?: PromotionData,
  ) => void;
  taxInfo?: React.ReactNode;
  promotion?: PromotionData;
};

export function PurchaseGameButton({
  title,
  purchaseOption,
  prices,
  isLoading,
  inCart,
  isMember,
  blocked = false,
  alreadyInCartText,
  addToCartAction,
  taxInfo,
  promotion,
}: Props) {
  const currentPrice = getPriceByType(prices, 'current');
  const disabled = isLoading || blocked || inCart || !currentPrice;

  const description = isLoading ? (
    <Loader label='...' />
  ) : inCart ? (
    <a
      className='focus-visible:ring-primary/20 hover:text-primary focus-visible:ring-4 focus-visible:outline-hidden'
      href={REDIRECT_CHECKOUT_ROUTE}
    >
      {alreadyInCartText}
    </a>
  ) : (
    <>
      <PurchaseDescription prices={prices} isMember={isMember} promotion={promotion} />
      {taxInfo}
    </>
  );

  const handleClick = () => {
    if (disabled) return;
    if (inCart) window.location.href = REDIRECT_CHECKOUT_ROUTE;
    const currentPrice = getPriceByType(prices, 'current');
    const currentPromotionalPrice = getPriceByType(promotion?.promotionalPrices || [], 'current');
    if (!currentPrice) return;
    addToCartAction(
      purchaseOption,
      currentPromotionalPrice ? currentPromotionalPrice : currentPrice,
      promotion,
    );
  };

  return (
    <ActionButtonWithDescription
      title={title}
      description={description}
      disabled={disabled}
      isLoading={isLoading}
      variant={disabled ? 'disabled' : 'purchase'}
      onClick={handleClick}
      className='min-h-20'
    />
  );
}
